<template>
	<div class="rel-clock-wrapper">
		<div v-if="large" class="rel-clock-labels">
			<span class="label-friendly">Friendly</span>
			<span class="label-frosty">Frosty</span>
		</div>
		<div :class="['rel-clock-chart', large ? 'rel-clock-large' : 'rel-clock-small']">
			<DoughnutChart :chartData="chartData" :options="chartOptions" class="chart" />
			<div class="rel-clock-center">
				<span class="zone-text" :style="{ color: zoneColor }">{{ zoneLabel }}</span>
			</div>
		</div>
		<div v-if="large" class="label-neutral">Neutral</div>
		<div v-if="large && mood" class="label-mood">{{ mood }}</div>
	</div>
</template>

<script lang="ts">
import { Chart, registerables } from "chart.js";
Chart.register(...registerables);
Chart.defaults.plugins.tooltip.enabled = false;
Chart.defaults.plugins.legend.display = false;
Chart.defaults.animation = false;

import { computed, defineComponent } from "vue";
import { DoughnutChart } from "vue-chart-3";

// 10 segments: 1-3 Frosty, 4-7 Neutral, 8-10 Friendly
const FROSTY_FILLED = "#4A7A9B";
const FROSTY_EMPTY = "#1E2E38";
const NEUTRAL_FILLED = "#7A7A90";
const NEUTRAL_EMPTY = "#2A2838";
const FRIENDLY_FILLED = "#3B7A57";
const FRIENDLY_EMPTY = "#1A3028";

function zoneOf(seg: number): "frosty" | "neutral" | "friendly" {
	if (seg <= 3) return "frosty";
	if (seg <= 7) return "neutral";
	return "friendly";
}

export default defineComponent({
	name: "RelationshipClock",
	components: { DoughnutChart },
	props: {
		value: { type: Number, default: 5 },
		large: { type: Boolean, default: false },
		mood: { type: String, default: "" },
	},
	setup(props) {
		const chartData = computed(() => {
			const colors: string[] = [];
			for (let i = 1; i <= 10; i++) {
				const zone = zoneOf(i);
				const isCurrent = i === props.value;
				if (isCurrent) {
					colors.push(
						zone === "frosty"
							? FROSTY_FILLED
							: zone === "neutral"
							? NEUTRAL_FILLED
							: FRIENDLY_FILLED
					);
				} else {
					colors.push(
						zone === "frosty" ? FROSTY_EMPTY : zone === "neutral" ? NEUTRAL_EMPTY : FRIENDLY_EMPTY
					);
				}
			}
			return {
				labels: Array(10).fill(""),
				datasets: [
					{
						data: Array(10).fill(1),
						backgroundColor: colors,
						borderColor: "#1A1A1F",
						borderWidth: 2,
						hoverOffset: 0,
					},
				],
			};
		});

		const chartOptions = {
			responsive: true,
			cutout: "40%",
			devicePixelRatio: 2,
			animation: false,
			plugins: { tooltip: { enabled: false }, legend: { display: false } },
		};

		const zoneLabel = computed(() => {
			const z = zoneOf(props.value);
			if (z === "frosty") return "FROSTY";
			if (z === "neutral") return "NEUTRAL";
			return "FRIENDLY";
		});

		const zoneColor = computed(() => {
			const z = zoneOf(props.value);
			if (z === "frosty") return FROSTY_FILLED;
			if (z === "neutral") return NEUTRAL_FILLED;
			return FRIENDLY_FILLED;
		});

		return { chartData, chartOptions, zoneLabel, zoneColor };
	},
});
</script>

<style scoped>
.rel-clock-wrapper {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 2px;
}

.rel-clock-labels {
	display: flex;
	justify-content: space-between;
	width: 100%;
	padding: 0 4px;
}

.label-friendly {
	font-family: "Share Tech Mono", monospace;
	font-size: 11px;
	letter-spacing: 0.08em;
	color: #3b7a57;
	text-transform: uppercase;
}

.label-frosty {
	font-family: "Share Tech Mono", monospace;
	font-size: 11px;
	letter-spacing: 0.08em;
	color: #4a7a9b;
	text-transform: uppercase;
}

.label-neutral {
	font-family: "Share Tech Mono", monospace;
	font-size: 11px;
	letter-spacing: 0.08em;
	color: #7a7a90;
	text-transform: uppercase;
	text-align: center;
	margin-top: 4px;
}

.label-mood {
	font-family: "Share Tech Mono", monospace;
	font-size: 13px;
	font-weight: 700;
	letter-spacing: 0.1em;
	color: #7a7a90;
	text-transform: uppercase;
	text-align: center;
	margin-top: 2px;
}

.rel-clock-chart {
	position: relative;
}

.rel-clock-small {
	width: 64px;
	height: 64px;
}

.rel-clock-large {
	width: 180px;
	height: 180px;
}

.chart {
	width: 100%;
	height: 100%;
}

.rel-clock-center {
	position: absolute;
	inset: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	pointer-events: none;
}

.zone-text {
	font-family: "Share Tech Mono", monospace;
	font-size: 7px;
	letter-spacing: 0.06em;
	text-transform: uppercase;
}

.rel-clock-large .zone-text {
	font-size: 12px;
}
</style>
